/**
 * The wallet log's search box: which entries a typed query leaves showing.
 *
 * Asked for as "a search field to the right of the filters, going through
 * notes and amounts as the user is typing, acting as an additional instant
 * filter". So it is one more narrowing of the rows, after the other four,
 * and like them it decides only what is SHOWN — the running column goes on
 * counting every entry.
 *
 * Matched per entry, not per row: a search is for "the entry where…", and an
 * entry's other legs are part of the answer. The mattress paid in euros and
 * billed in forints is found by either figure, from either wallet's log.
 *
 * Notes are the entry's note (`description`, which the quick form calls
 * Note), its reference, and every leg's memo — wherever somebody wrote words
 * about it. Case and accents are ignored: "kozos" finds "közös", because a
 * search box that needs the accent typed exactly is one that misses.
 *
 * Amounts are read the way people type them — from the left, sign ignored,
 * any of space, comma, dot or apostrophe between thousands:
 *
 *  - `379`, `379 225`, `379,225` find 379,225.52 — a prefix of the whole part.
 *  - `225.5` or `225,5` find 225.52 and not 379,225.52: as a decimal mark
 *    it ends the whole part, so that must be all of it, and the fraction
 *    begins with what was typed.
 *  - …and also anything whose whole part begins 2255: one or two digits
 *    after a mark is just as likely the first of a thousands group still
 *    being typed. Reading it only as a decimal emptied the list half-way
 *    through typing "379,225" — at "379,2" — which is exactly when a search
 *    as you type is being looked at.
 *
 * With or without the separators, then, the same amounts are found. Three
 * digits after the last mark read as thousands only — "1,500" is fifteen
 * hundred in the books this is used for — and an amount is never matched
 * through its fraction alone, so "1500" does not find 15.00.
 *
 * A query made only of digits and separators is one amount, spaces and all.
 * Anything else is words, every one of which must be found: a word that is a
 * number may be an amount or part of a note ("invoice 2231").
 */

export interface LogSearchEntry {
  description?: string | null;
  reference?: string | null;
  legs: ReadonlyArray<{ memo?: string | null; amount: string; value: string }>;
}

/** Lower case, accents off — the form both sides are compared in. */
export function foldForSearch(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

const NUMERIC = /^[+-]?\s*\d[\d\s.,']*$/;

interface AmountQuery {
  /** Every digit typed, read as the start of the whole part. */
  digits: string;
  /** When the query ends in a mark and up to two digits: the decimal reading. */
  whole: string;
  fraction: string | null;
}

/** How a numeric query reads as an amount, or `null` when it does not. */
export function amountQuery(raw: string): AmountQuery | null {
  const text = raw.trim();
  if (!NUMERIC.test(text)) return null;
  const body = text.replace(/^[+-]\s*/, '');
  const digits = body.replace(/\D/g, '');
  const decimal = /^(.*?)[.,](\d{0,2})$/.exec(body);
  if (decimal) return { digits, whole: decimal[1]!.replace(/\D/g, ''), fraction: decimal[2]! };
  return { digits, whole: digits, fraction: null };
}

/** Whether `amount` (a decimal string, any sign) is what `q` was typed towards. */
export function amountMatches(amount: string, q: AmountQuery): boolean {
  const n = Math.abs(Number(amount));
  if (!Number.isFinite(n)) return false;
  const [whole, fraction] = n.toFixed(2).split('.') as [string, string];
  if (q.digits !== '' && whole.startsWith(q.digits)) return true;
  return q.fraction !== null && (q.whole === '' || whole === q.whole) && fraction.startsWith(q.fraction);
}

/**
 * A matcher for one query, built once per keystroke and asked once per
 * entry — the folding and parsing are done here, not per row. `null` when
 * the query is empty, which is "no search", not "match nothing".
 */
export function logSearchMatcher(query: string): ((entry: LogSearchEntry) => boolean) | null {
  const trimmed = query.trim();
  if (trimmed === '') return null;
  const textOf = (e: LogSearchEntry): string => foldForSearch(
    [e.description, e.reference, ...e.legs.map((l) => l.memo)].filter(Boolean).join('\n'));
  const anyAmount = (e: LogSearchEntry, q: AmountQuery): boolean =>
    e.legs.some((l) => amountMatches(l.amount, q) || amountMatches(l.value, q));

  const whole = amountQuery(trimmed);
  if (whole) {
    const folded = foldForSearch(trimmed);
    return (e) => anyAmount(e, whole) || textOf(e).includes(folded);
  }
  const words = trimmed.split(/\s+/).map((w) => ({ folded: foldForSearch(w), amount: amountQuery(w) }));
  return (e) => {
    const text = textOf(e);
    return words.every((w) => text.includes(w.folded) || (w.amount !== null && anyAmount(e, w.amount)));
  };
}
